import {
  ContactMail,
  MoreVert,
  RefreshOutlined,
  Reply,
  Route,
  Search,
  UnsubscribeOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import ActivityTimeline from "../../components/ActivityTimeline";
import ProgressBar from "../../components/ProgressBar";
import SequenceMetricsCards from "../../components/SequenceMetricsCards";
import { DEMO_MODE_KEY } from "../../constants";
import { DEMO_SEQUENCES, DEMO_SEQUENCE_METRICS } from "../../constants/demoData";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { Company, Employee } from "../../types";
import { hostname, isDevelopment, parseError, User } from "../../utils";
import GenerateSequenceDialog from "./GenerateSequenceDialog";
import ReplyManagementDialog from "./ReplyManagementDialog";
import SenderProfiles from "./SenderProfiles";
import SequenceEditor from "./SequenceEditor";
import SequenceMetricsDialog from "./SequenceMetricsDialog";
import SequenceTypeDialog from "./SequenceTypeDialog";
import ImportFromSmartListDialog from "./dialogs/ImportFromSmartListDialog";
import SequenceActionsDialog from "./dialogs/SequenceActionsDialog";
import SequenceMembersDialog from "./dialogs/SequenceMembersDialog";
import UnsubscribedDialog from "./dialogs/UnsubscribedDialog";

export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  employees,
  isSuperUser = false,
}: {
  getToken: () => Promise<string>;
  openDialog: (
    title: string,
    content: string,
    buttonText: string,
    onClick: () => void
  ) => void;
  eventListener: (event: any) => void;
  currentUser: any | User;
  companyId: string;
  company: Company;
  employees: Employee[];
  isSuperUser?: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { whitelabelSettings } = useWhitelabel();
  const [sequences, setSequences] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const isSearchTriggeredRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [executionsOpen, setExecutionsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSequence, setSelectedSequence] = useState<any>(null);
  const [sequenceTypeOpen, setSequenceTypeOpen] = useState(false);
  const [generatedSequenceOpen, setGenerateSequenceOpen] = useState(false);
  const [senderProfilesOpen, setSenderProfilesOpen] = useState(false);
  const [unsubscribedOpen, setUnsubscribedOpen] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [replyManagementOpen, setReplyManagementOpen] = useState(false);
  const [importFromListOpen, setImportFromListOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [metrics, setMetrics] = useState<any>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  // Listen for demo mode changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };

    // Also check periodically for same-tab changes (storage event only fires for other tabs)
    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => {
        if (prev !== currentValue) {
          return currentValue;
        }
        return prev;
      });
    };

    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const chooseSequenceType = (type: string) => {
    if (type === "ai") {
      setGenerateSequenceOpen(true);
    } else {
      setSelectedSequence(null);
      setEditorOpen(true);
    }
    setSequenceTypeOpen(false);
  };

  const onGeneratedSequence = (sequence: any) => {
    setSelectedSequence({ name: sequence.name, steps: sequence.steps });
    setGenerateSequenceOpen(false);
    setEditorOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleSequenceActive = async (sequence: any, active: boolean) => {
    const currentToken = await getToken();
    try {
      setLoading(true);
      await axios.post(
        `${hostname}/sequences`,
        {
          ...sequence,
          active: active,
          companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      refreshSequences();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteSequence = async (sequenceId: string) => {
    const currentToken = await getToken();
    openDialog(
      "Delete Sequence",
      `Are you sure you want to delete this sequence?`,
      "Confirm",
      () => {
        setLoading(true);
        return axios
          .delete(`${hostname}/sequences/${sequenceId}`, {
            params: { companyId },
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            setLoading(false);
            refreshSequences();
          });
      }
    );
  };

  const duplicateSequence = async (sequenceId: string) => {
    const currentToken = await getToken();
    try {
      setLoading(true);
      await axios.post(
        `${hostname}/sequences/${sequenceId}/duplicate`,
        { companyId },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      eventListener({
        type: "SET_SUCCESS",
        success: "Sequence duplicated successfully",
      });
      refreshSequences();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSequences = async () => {
    // Use demo data if demo mode is enabled
    if (isDemoMode) {
      setRefreshing(true);
      setTimeout(() => {
        setSequences(DEMO_SEQUENCES as any);
        setTotal(DEMO_SEQUENCES.length);
        setRefreshing(false);
      }, 300);
      return;
    }

    setRefreshing(true);
    const currentToken = await getToken();
    axios
      .get(`${hostname}/sequences`, {
        params: { companyId, page: page + 1, pageSize, search: searchQuery },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setSequences(res.data.items);
        setTotal(res.data.total);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const loadSequences = async () => {
    setLoading(true);
    await refreshSequences();
    setLoading(false);
  };

  const loadMetrics = async () => {
    // Use demo data if demo mode is enabled
    if (isDemoMode) {
      setMetricsLoading(true);
      setTimeout(() => {
        setMetrics(DEMO_SEQUENCE_METRICS);
        setMetricsLoading(false);
      }, 300);
      return;
    }

    setMetricsLoading(true);
    const currentToken = await getToken();
    try {
      const res = await axios.get(`${hostname}/sequences/metrics`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setMetrics(res.data);
    } catch (err) {
      console.error("Error loading sequence metrics:", err);
    } finally {
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser.uid && !isSearchTriggeredRef.current) {
      refreshSequences();
    }
    // Reset the flag after handling
    isSearchTriggeredRef.current = false;
  }, [page, isDemoMode]);

  useEffect(() => {
    if (currentUser.uid) {
      // Debounce the search to avoid too many requests
      const timeoutId = setTimeout(() => {
        isSearchTriggeredRef.current = true; // Set flag to prevent duplicate request
        setPage(0); // Reset to first page when searching
        refreshSequences();
      }, 500); // 500ms delay

      // Cleanup function to cancel the timeout if dependencies change
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (currentUser.uid) {
      loadSequences();
      loadMetrics();
    }
  }, [currentUser.uid, companyId, isDemoMode]);

  return (
    <Box>
      <Box sx={{ mt: 2, mb: 2 }} data-tutorial="sequence-analytics">
        <SequenceMetricsCards
          totals={metrics?.totals || {}}
          showPendingApprovals={true}
          onPendingApprovalsClick={() => setApprovalsOpen(true)}
          loading={metricsLoading}
        />
      </Box>

      <ActivityTimeline
        sequenceId={null}
        companyId={companyId}
        getToken={getToken}
        title="All Sequences Activity (Last 3 Days)"
        enabled={!!currentUser.uid}
      />

      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Button
          data-tutorial="create-sequence"
          variant="contained"
          onClick={() => setSequenceTypeOpen(true)}
          startIcon={<Route />}
          sx={{ minWidth: { xs: "auto", sm: "auto" }, flexShrink: 0 }}
        >
          New Sequence
        </Button>
        <Button
          variant="outlined"
          onClick={() => setSenderProfilesOpen(true)}
          startIcon={<ContactMail />}
          sx={{ minWidth: { xs: "auto", sm: "auto" }, flexShrink: 0 }}
        >
          Sender Profiles
        </Button>
        <Button
          variant="outlined"
          onClick={() => setUnsubscribedOpen(true)}
          startIcon={<UnsubscribeOutlined />}
          sx={{ minWidth: { xs: "auto", sm: "auto" }, flexShrink: 0 }}
        >
          Unsubscribed
        </Button>
        {isDevelopment() && (
          <Button
            variant="outlined"
            onClick={() => setReplyManagementOpen(true)}
            startIcon={<Reply />}
            sx={{ minWidth: { xs: "auto", sm: "auto" }, flexShrink: 0 }}
          >
            Reply Management
          </Button>
        )}
        <TextField
          placeholder="Search sequences by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, maxWidth: isMobile ? "none" : 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Paper style={{ marginTop: 20, marginBottom: 40 }} variant="outlined">
        {refreshing && <LinearProgress />}
        {!refreshing && <div style={{ height: 4 }}></div>}
        <IconButton
          onClick={() => refreshSequences()}
          style={{ float: "right", marginRight: 5 }}
        >
          <RefreshOutlined />
        </IconButton>
        <TableContainer data-tutorial="sequence-list">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Members</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sequences.map((sequence: any) => (
                <TableRow
                  key={sequence.id}
                  onClick={() => {
                    setSelectedSequence(sequence);
                    setEditorOpen(true);
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Switch
                      checked={sequence.active !== false}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedSequence(null);
                        toggleSequenceActive(sequence, e.target.checked);
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{sequence.name}</TableCell>
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    <ProgressBar
                      value={
                        sequence.memberCount > 0 && sequence.stepCount > 0
                          ? (sequence.executedStepCount /
                              (sequence.memberCount * sequence.stepCount)) *
                            100
                          : 0
                      }
                      total={
                        (sequence.memberCount || 0) * (sequence.stepCount || 0)
                      }
                      completed={sequence.executedStepCount || 0}
                      stats={{
                        totalMembers: sequence.memberCount || 0,
                        completed: sequence.completedMemberCount || 0,
                        inProgress: sequence.inProgressMemberCount || 0,
                        notStarted: sequence.notStartedMemberCount || 0,
                        bounced: sequence.bouncedMemberCount || 0,
                        unsubscribed: sequence.unsubscribedMemberCount || 0,
                        prospectValue: sequence.prospectValue || 0,
                        totalReplies: sequence.totalReplies || 0,
                        totalBookings: sequence.totalBookings || 0,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {sequence.created_date
                      ? moment(sequence.created_date).format(
                          "MMMM Do YYYY, h:mm:ss a"
                        )
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {sequence.updated_date
                      ? moment(sequence.updated_date).format(
                          "MMMM Do YYYY, h:mm:ss a"
                        )
                      : "-"}
                  </TableCell>
                  <TableCell>{sequence.memberCount}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setSelectedId(sequence.id);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={selectedId === sequence.id}
                      onClose={(e: any) => {
                        e.stopPropagation();
                        setAnchorEl(null);
                        setSelectedId(null);
                      }}
                    >
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSequence(sequence);
                          setEditorOpen(true);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSequence(sequence);
                          setMetricsOpen(true);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Metrics
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSequence(sequence);
                          setExecutionsOpen(true);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Actions
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSequence(sequence);
                          setMembersOpen(true);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Members
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSequence(sequence);
                          setImportFromListOpen(true);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Import from Smart List
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateSequence(sequence.id);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Duplicate
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSequence(sequence.id);
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      {/* Dialog Components */}
      <SequenceTypeDialog
        open={sequenceTypeOpen}
        onClose={() => setSequenceTypeOpen(false)}
        onChoose={chooseSequenceType}
      />
      <SequenceEditor
        open={editorOpen}
        sequence={selectedSequence}
        onClose={() => {
          setEditorOpen(false);
          setSelectedSequence(null);
          refreshSequences();
        }}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        superUser={isSuperUser}
      />
      <GenerateSequenceDialog
        open={generatedSequenceOpen}
        onClose={() => setGenerateSequenceOpen(false)}
        onSequence={onGeneratedSequence}
        companyId={companyId}
        getToken={getToken}
      />
      <SequenceMembersDialog
        open={membersOpen}
        onClose={() => {
          setMembersOpen(false);
          setSelectedSequence(null);
          refreshSequences();
        }}
        companyId={companyId}
        sequence={selectedSequence}
        getToken={getToken}
      />
      <SequenceActionsDialog
        open={executionsOpen}
        onClose={() => {
          setExecutionsOpen(false);
          setSelectedSequence(null);
          refreshSequences();
        }}
        companyId={companyId}
        sequence={selectedSequence}
        getToken={getToken}
        initialFilter="all"
      />
      <SequenceActionsDialog
        open={approvalsOpen}
        onClose={() => setApprovalsOpen(false)}
        companyId={companyId}
        sequence={null}
        getToken={getToken}
        initialFilter="unapproved"
        showAllSequences={true}
      />
      <UnsubscribedDialog
        open={unsubscribedOpen}
        onClose={() => setUnsubscribedOpen(false)}
        companyId={companyId}
        getToken={getToken}
        onError={(error) => eventListener({ type: "SET_ERROR", error })}
      />
      <SenderProfiles
        open={senderProfilesOpen}
        onClose={() => {
          setSenderProfilesOpen(false);
          refreshSequences();
        }}
        companyId={companyId}
        eventListener={eventListener}
        getToken={getToken}
        employees={employees}
        company={company}
      />
      <SequenceMetricsDialog
        open={metricsOpen}
        onClose={() => {
          setMetricsOpen(false);
          setSelectedSequence(null);
          refreshSequences();
        }}
        companyId={companyId}
        sequenceId={selectedSequence?.id}
        getToken={getToken}
        eventListener={eventListener}
      />
      <ReplyManagementDialog
        open={replyManagementOpen}
        onClose={() => setReplyManagementOpen(false)}
        getToken={getToken}
        companyId={companyId}
        eventListener={eventListener}
      />
      <ImportFromSmartListDialog
        open={importFromListOpen}
        onClose={() => {
          setImportFromListOpen(false);
          setSelectedSequence(null);
          refreshSequences();
        }}
        sequence={selectedSequence}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        onViewMembers={() => {
          // Keep selectedSequence so SequenceMembersDialog can render it.
          setImportFromListOpen(false);
          setMembersOpen(true);
          refreshSequences();
        }}
      />
    </Box>
  );
};
